/**
 * Soulo Wishlist — browser-side persistence (localStorage).
 * Architecture allows later replacement with account/server sync.
 * Does not claim cross-device synchronization.
 */
(function () {
  const STORAGE_KEY = 'soulo_wishlist_v1';
  const EVENT_NAME = 'soulo:wishlist-changed';

  function read() {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch (e) {
      return [];
    }
  }

  function write(items) {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    window.dispatchEvent(new CustomEvent(EVENT_NAME, { detail: { items } }));
  }

  function normalizeHandle(handle) {
    return String(handle || '')
      .trim()
      .toLowerCase();
  }

  const SouloWishlist = {
    getAll() {
      return read();
    },

    count() {
      return read().length;
    },

    has(handle) {
      const h = normalizeHandle(handle);
      return read().some((item) => item.handle === h);
    },

    add(product) {
      if (!product || !product.handle) return false;
      const items = read();
      const handle = normalizeHandle(product.handle);
      if (items.some((item) => item.handle === handle)) return false;
      items.unshift({
        handle,
        id: product.id || null,
        title: product.title || '',
        url: product.url || `/products/${handle}`,
        image: product.image || '',
        price: product.price || '',
        compareAtPrice: product.compareAtPrice || '',
        available: product.available !== false,
        variantId: product.variantId || null,
        addedAt: Date.now(),
      });
      write(items);
      return true;
    },

    remove(handle) {
      const h = normalizeHandle(handle);
      const next = read().filter((item) => item.handle !== h);
      write(next);
      return true;
    },

    toggle(product) {
      if (!product || !product.handle) return false;
      if (this.has(product.handle)) {
        this.remove(product.handle);
        return false;
      }
      this.add(product);
      return true;
    },

    clear() {
      write([]);
    },
  };

  window.SouloWishlist = SouloWishlist;

  function productFromButton(btn) {
    return {
      handle: btn.dataset.productHandle,
      id: btn.dataset.productId,
      title: btn.dataset.productTitle,
      url: btn.dataset.productUrl,
      image: btn.dataset.productImage,
      price: btn.dataset.productPrice,
      compareAtPrice: btn.dataset.productCompare,
      available: btn.dataset.productAvailable !== 'false',
      variantId: btn.dataset.variantId,
    };
  }

  function syncButton(btn) {
    const handle = btn.dataset.productHandle;
    const active = SouloWishlist.has(handle);
    btn.classList.toggle('is-active', active);
    btn.setAttribute('aria-pressed', active ? 'true' : 'false');
    const labelAdd = btn.dataset.labelAdd || 'Add to wishlist';
    const labelRemove = btn.dataset.labelRemove || 'Remove from wishlist';
    btn.setAttribute('aria-label', active ? labelRemove : labelAdd);
  }

  function syncCounts() {
    const count = String(SouloWishlist.count());
    document.querySelectorAll('[data-soulo-wishlist-count]').forEach((el) => {
      el.textContent = count === '0' ? '' : count;
      el.setAttribute('data-count', count);
    });
  }

  function syncAllButtons() {
    document.querySelectorAll('[data-soulo-wishlist-toggle]').forEach(syncButton);
    syncCounts();
  }

  function onClick(event) {
    const btn = event.target.closest('[data-soulo-wishlist-toggle]');
    if (!btn) return;
    event.preventDefault();
    event.stopPropagation();
    SouloWishlist.toggle(productFromButton(btn));
    syncAllButtons();
    if (typeof btn.dataset.refreshPage !== 'undefined') {
      renderWishlistPage();
    }
  }

  async function addToCart(variantId, quantity) {
    if (!variantId) return false;
    const response = await fetch(window.Shopify?.routes?.root
      ? `${window.Shopify.routes.root}cart/add.js`
      : '/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [{ id: Number(variantId), quantity: quantity || 1 }] }),
    });
    return response.ok;
  }

  function escapeHtml(value) {
    return String(value == null ? '' : value).replace(/[&<>"']/g, (ch) => {
      switch (ch) {
        case '&': return '&amp;';
        case '<': return '&lt;';
        case '>': return '&gt;';
        case '"': return '&quot;';
        case "'": return '&#39;';
        default: return ch;
      }
    });
  }

  // Force Latin digits and drop a trailing ".00" so wishlist prices match
  // the same format used everywhere else (snippets/price.liquid) — see
  // that file's comment for why this normalization exists.
  const ARABIC_DIGIT_MAP = {
    '٠': '0', '١': '1', '٢': '2', '٣': '3', '٤': '4', '٥': '5', '٦': '6', '٧': '7', '٨': '8', '٩': '9',
    '۰': '0', '۱': '1', '۲': '2', '۳': '3', '۴': '4', '۵': '5', '۶': '6', '۷': '7', '۸': '8', '۹': '9',
    '٫': '.',
  };

  function normalizeMoneyDigits(str) {
    return str.replace(/[٠-٩۰-۹٫]/g, (ch) => ARABIC_DIGIT_MAP[ch] || ch).replace('.00', '');
  }

  // Minimal reimplementation of Shopify.formatMoney's placeholder substitution
  // (amount / amount_no_decimals / amount_with_comma_separator) — used as the
  // primary path since window.Shopify.formatMoney is not guaranteed to be
  // loaded on every route (it silently fell back to a bare number here).
  function applyMoneyFormat(cents, format) {
    const n = Number(cents) / 100;
    if (Number.isNaN(n)) return String(cents);
    const placeholder = /\{\{\s*(\w+)\s*\}\}/;
    const match = format.match(placeholder);
    if (!match) return format.replace(placeholder, n.toFixed(2));
    let value;
    switch (match[1]) {
      case 'amount_no_decimals':
        value = Math.round(n).toString();
        break;
      case 'amount_with_comma_separator':
        value = n.toFixed(2).replace('.', ',');
        break;
      default:
        value = n.toFixed(2);
    }
    return format.replace(placeholder, value);
  }

  function moneyFormat(cents) {
    if (cents === '' || cents == null) return '';
    const root = document.querySelector('[data-soulo-wishlist-page]');
    const format = root && root.dataset.moneyFormat;
    if (format) {
      return normalizeMoneyDigits(applyMoneyFormat(cents, format));
    }
    try {
      if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
        return normalizeMoneyDigits(window.Shopify.formatMoney(cents));
      }
    } catch (e) { /* ignore */ }
    const n = Number(cents);
    if (Number.isNaN(n)) return String(cents);
    return normalizeMoneyDigits((n / 100).toFixed(2));
  }

  function renderWishlistPage() {
    const root = document.querySelector('[data-soulo-wishlist-page]');
    if (!root) return;

    const empty = root.querySelector('[data-soulo-wishlist-empty]');
    const grid = root.querySelector('[data-soulo-wishlist-grid]');
    const items = SouloWishlist.getAll();

    if (!items.length) {
      if (empty) empty.hidden = false;
      if (grid) {
        grid.hidden = true;
        grid.innerHTML = '';
      }
      return;
    }

    if (empty) empty.hidden = true;
    if (!grid) return;
    grid.hidden = false;

    const labels = {
      remove: root.dataset.labelRemove || 'Remove',
      addToCart: root.dataset.labelAddToCart || 'Add to cart',
      unavailable: root.dataset.labelUnavailable || 'Unavailable',
      view: root.dataset.labelView || 'View',
    };

    grid.innerHTML = items
      .map((item) => {
        const price = moneyFormat(item.price);
        const compare = item.compareAtPrice ? moneyFormat(item.compareAtPrice) : '';
        const canAdd = item.available && item.variantId;
        const handle = escapeHtml(item.handle);
        const url = escapeHtml(item.url);
        const image = escapeHtml(item.image);
        const title = escapeHtml(item.title || item.handle);
        const variantId = escapeHtml(item.variantId);
        const labelRemove = escapeHtml(labels.remove);
        return `
          <article class="soulo-wishlist-item soulo-product-card" data-handle="${handle}">
            <a class="soulo-product-card__media" href="${url}">
              ${item.image ? `<img src="${image}" alt="${title}" loading="lazy" width="600" height="800">` : ''}
            </a>
            <div class="soulo-product-card__meta">
              <h3 class="soulo-product-card__title"><a href="${url}">${title}</a></h3>
              <div class="soulo-product-card__price">
                ${price ? `<span>${escapeHtml(price)}</span>` : ''}
                ${compare ? `<s>${escapeHtml(compare)}</s>` : ''}
              </div>
              <div class="soulo-wishlist-item__actions">
                ${canAdd
                  ? `<button type="button" class="soulo-btn" data-soulo-wishlist-atc data-variant-id="${variantId}">${escapeHtml(labels.addToCart)}</button>`
                  : `<span class="soulo-copy">${item.available === false ? escapeHtml(labels.unavailable) : escapeHtml(labels.view)}</span>`}
                <button type="button" class="soulo-btn soulo-btn--ghost" data-soulo-wishlist-toggle data-refresh-page
                  data-product-handle="${handle}"
                  data-label-add="${labelRemove}"
                  data-label-remove="${labelRemove}">${labelRemove}</button>
              </div>
            </div>
          </article>`;
      })
      .join('');

    syncAllButtons();
  }

  document.addEventListener('click', async (event) => {
    onClick(event);
    const atc = event.target.closest('[data-soulo-wishlist-atc]');
    if (!atc) return;
    event.preventDefault();
    const ok = await addToCart(atc.dataset.variantId, 1);
    if (ok) {
      atc.textContent = atc.dataset.labelAdded || 'Added';
      document.dispatchEvent(new CustomEvent('cart:refresh'));
    }
  });

  window.addEventListener(EVENT_NAME, () => {
    syncAllButtons();
    renderWishlistPage();
  });

  document.addEventListener('DOMContentLoaded', () => {
    syncAllButtons();
    renderWishlistPage();
  });

  // Expose for theme editor / section re-renders
  document.addEventListener('shopify:section:load', () => {
    syncAllButtons();
    renderWishlistPage();
  });
})();
